/**
 * Entity logo (customer, deployed service, …): rounded-square image when
 * uploaded, otherwise a neutral glyph. Sits next to names in lists/headers.
 */
export function EntityLogo({
  name,
  url,
  size = 26,
  fallbackIcon = "fa-building",
}: {
  name: string;
  url: string | null;
  size?: number;
  fallbackIcon?: string;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt={`${name} logo`}
        width={size}
        height={size}
        className="shrink-0 rounded-md border border-border-light bg-white object-contain"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="grid shrink-0 place-items-center rounded-md border border-border-light bg-bg-secondary text-text-muted"
      style={{ width: size, height: size, fontSize: size * 0.45 }}
    >
      <i className={`fa-solid ${fallbackIcon}`} />
    </span>
  );
}

import { useEffect, useRef, useState } from "react";

/**
 * Editable logo: the logo itself is the control. Click it — with no logo,
 * the file picker opens directly; with one, a small popover offers
 * "Upload new" / "Remove". No stray delete buttons in the layout.
 */
export function EditableLogo({
  name,
  url,
  size = 44,
  fallbackIcon = "fa-building",
  uploading,
  onUpload,
  onRemove,
}: {
  name: string;
  url: string | null;
  size?: number;
  fallbackIcon?: string;
  uploading?: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menuOpen]);

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => (url ? setMenuOpen((v) => !v) : fileRef.current?.click())}
        title={url ? "Change logo" : "Upload logo"}
        className="group relative block"
      >
        <EntityLogo name={name} url={url} size={size} fallbackIcon={fallbackIcon} />
        <span
          className={`absolute inset-0 grid place-items-center rounded-md bg-black/50 text-white transition-opacity ${
            uploading || menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          style={{ fontSize: size * 0.28 }}
        >
          <i
            className={`fa-solid ${
              uploading ? "fa-circle-notch fa-spin" : url ? "fa-pen" : "fa-camera"
            }`}
          />
        </span>
      </button>

      {menuOpen && (
        <div className="absolute left-0 top-full z-20 mt-1.5 w-40 overflow-hidden rounded-token-md border border-border bg-bg-elevated py-1 shadow-token-lg">
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              fileRef.current?.click();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-text-secondary hover:bg-bg-card-hover hover:text-text-heading"
          >
            <i className="fa-solid fa-arrow-up-from-bracket w-3.5 text-center" />
            Upload new
          </button>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onRemove();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-text-secondary hover:bg-bg-card-hover hover:text-danger"
          >
            <i className="fa-solid fa-trash w-3.5 text-center" />
            Remove
          </button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onUpload(f);
        }}
      />
    </div>
  );
}
